import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { filter, firstValueFrom, take } from 'rxjs';
import { MapHelperService } from 'src/services/map-helper.service';
import { FeatureCollection, Polygon } from '@turf/helpers';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { LayerAPIService } from 'src/app/features/shared/services/API/LayersAPI.service';
import { PlaceLocationDetailsPopUpComponent } from '../../locations-section/components/3-panel-detail-locations/organisms/location-popup-details/place-location-detail-popup/place-location-details-popup.component';
import { HierarchyLevel } from '../../../ui/organisms/polygon-selector-with-levels/models/polygon-selector-with-levels.models';
import { EnumPolygonFillType } from '../../../ui/organisms/polygon-selector/helpers/enums/EnumPolygonFillType.enum';

@Injectable({
  providedIn: 'root'
})
export class PotentialZonesLayerControlService {
  hierarchyLevels: HierarchyLevel[] = [];

  private sourcePrefix = 'polygon-selector-source-potential-zones-';
  private layerPrefix = 'layer-polygon-selector-potential-zones-';
  private layerLinePrefix = 'layer-line-polygon-selector-potential-zones-';
  private layerTextPrefix = 'layer-polygon-selector-text-potential-zones-';

  private locationSufix = 'agebs-locations';

  private levelLineColors: string[] = ['rgb(140, 0, 255)', 'rgb(34, 59, 83)', '#e55e5e', '#1329f0', '#51bbd6',];
  private levelFillColors: string[] = ['rgb(160, 200, 240)', 'rgb(144, 224, 240)', '#f9f4a5', '#627e90', '#f28b82'];

  private isInitialized: boolean = false;

  popUp: any = null;
  hoveredMarker: any = null;
  hoveredMarkerFeature: any = null;

  constructor(
    private mapHelperService: MapHelperService,
    private http: HttpClient,
    private layerAPIService: LayerAPIService,
    private dialog: MatDialog,
    private toastrService: ToastrService
  ) {}

  // configure(sourceAdd: string): void {
  //   this.sourcePrefix = this.sourcePrefix + sourceAdd + '-';
  //   this.layerPrefix = this.layerPrefix + sourceAdd + '-';
  //   this.layerLinePrefix = this.layerLinePrefix + sourceAdd + '-';
  //   this.layerTextPrefix = this.layerTextPrefix + sourceAdd + '-';
  // }

  async loadLayersFromAPI(idMap: number): Promise<HierarchyLevel[]> {
    const resp = await firstValueFrom( this.layerAPIService.GetLayersByIdMap(idMap.toString()).pipe(take(1)) );

    if (!resp.isSuccess) {
      console.error("selector, error cargando capas desde API", resp);
      return [];
    }

    const layers = resp.result as any[];
    this.hierarchyLevels = [];

    const sortedLayers = layers.sort((a, b) => (a.idOrder || 0) - (b.idOrder || 0));

    sortedLayers.forEach((layer, index) => {
      const hierarchyLevel: HierarchyLevel = { 
        key: layer.name || layer.id.toString(),
        path: layer.path || '',
        parentIdProperty: null,
        idProperty: layer.layerFilterControl_PropertyFilter || 'Id',
        sourceName: this.getSourceName(index),
        layerName: this.getLayerName(index),
        layerLineName: this.getLayerLineName(index),
        layerTextName: this.getLayerTextName(index)
      };
      this.hierarchyLevels.push(hierarchyLevel);
    });

    return this.hierarchyLevels;
  }

  async loadLayersFromAssetsDummy(): Promise<HierarchyLevel[]> {
    this.hierarchyLevels = [
      { 
        key: 'regions', 
        path: 'assets/regions_geoJson_dummy/regiones/regiones.geojson', 
        parentIdProperty: null, 
        idProperty: 'Id',
        sourceName: this.getSourceName(0),
        layerName: this.getLayerName(0),
        layerLineName: this.getLayerLineName(0),
        layerTextName: this.getLayerTextName(0),
      },
      { 
        key: 'agencies', 
        path: 'assets/regions_geoJson_dummy/zonas/zonas.geojson', 
        parentIdProperty: 'RegionId', 
        idProperty: 'Id',
        sourceName: this.getSourceName(1),
        layerName: this.getLayerName(1),
        layerLineName: this.getLayerLineName(1),
        layerTextName: this.getLayerTextName(1),
      }
    ];
    
    return this.hierarchyLevels;
  }

  getHierarchyLevels(): HierarchyLevel[] {
    return this.hierarchyLevels;
  }

  async createAllSourcesAndLayers(agebCloropetConfig): Promise<void> {
    // if (!this.mapHelperService._map || !this.mapHelperService.isMainSourceReady) {
    //   console.error('LayerControl: Mapa no está listo');
    //   return;
    // }

    if (!this.mapHelperService._map || !this.mapHelperService.isMainSourceReady || !this.mapHelperService.isStyleReady) {
        await firstValueFrom(
          this.mapHelperService.mainSource$.pipe(
            filter(state => state != null && state.isMainSourceReady && state.isStyleReady),
            take(1)
          )
        );
      }

    if (this.isInitialized) {
      return;
    }

    // crea sources y layers para los niveles iniciales
    this.hierarchyLevels.forEach((level, index) => {
      if (index >= this.hierarchyLevels.length -1)
        return;
      this.createSourceAndLayersForLevel(index);
    });

    //y el de la capa final
    this.createSimpleSelectorSourceAndLayers('agebs-selector', EnumPolygonFillType.Cloropet, '#627e90', agebCloropetConfig );
    this.createLocationsSourceAndLayers(this.locationSufix);
    this.isInitialized = true;
  }

  private createSourceAndLayersForLevel(levelIndex: number): void {
    var sourceDisplayName = "";
    var sourceIcon = "";
    switch (levelIndex) {
      case 0: 
        sourceDisplayName = "Region seleccionada";
        sourceIcon = 'annotation-potential-zones-region.png';
        break;
      case 1:
        sourceDisplayName = "Zona seleccionada";
        sourceIcon = 'annotation-potential-zones-zone.png';
        break;
      default:
        sourceDisplayName = "Zonas potenciales nivel " + levelIndex;
        sourceIcon = 'annotation-polygon-selector.png';
        break;
    }
    const sourceName = this.getSourceName(levelIndex);
    const layerName = this.getLayerName(levelIndex);
    const layerLineName = this.getLayerLineName(levelIndex);
    const layerTextName = this.getLayerTextName(levelIndex);

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);
    
    if (!existingSource) {
      const color = this.getColorForLevel(levelIndex, this.levelFillColors);
      const lineColor = this.getColorForLevel(levelIndex, this.levelLineColors);

      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, sourceDisplayName, sourceIcon);
      this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Polígonos de zonas potenciales, nivel " + levelIndex, color);
      this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de zonas potenciales, nivel " + levelIndex, lineColor, 3);
      this.mapHelperService.AddLayerText(layerTextName, sourceName, 'Description', "Etiqueta de zonas potenciales, nivel " + levelIndex, -1);
      
    } else {
      console.log("ya existe el source para nivel " + levelIndex);
    }
  }

  // crear source y layers para selector simple 
  createSimpleSelectorSourceAndLayers(sourceNameAdd: string, fillType: string = 'solid', fillColor: string = '#627e90', cloropetConfig: any = null): void {
    const sourceName = 'polygon-selector-source-' + sourceNameAdd;
    const layerName = 'layer-polygon-selector-' + sourceNameAdd;
    const layerLineName = 'layer-line-polygon-selector-' + sourceNameAdd;
    const layerTextName = 'layer-polygon-selector-text-' + sourceNameAdd;
    const lineColor = '#352F84';

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);
    
    if (!existingSource) {
      this.mapHelperService.AddSourceEmptyGeoJson(sourceName, "Agebs", 'annotation-potential-zones-agebs.png');
      
      if (fillType == EnumPolygonFillType.Cloropet && cloropetConfig) {
        this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Poligonos de selección de agebs", fillColor, cloropetConfig);
      } else {
        this.mapHelperService.AddLayerPolygonsWithFeatureStatus(layerName, sourceName, "Poligonos de selección de agebs", fillColor);
        this.mapHelperService.AddLayerLine(layerLineName, sourceName, "Líneas de selección de agebs", lineColor, 3);
      }
      
      this.mapHelperService.AddLayerText(layerTextName, sourceName,  'Description', "Etiqueta de selección de agebs", -1);
      
    } else {
      console.log("ya existe el source simple");
    }
  }
  
  createLocationsSourceAndLayers(sourceNameAdd: string): void {
    const sourceName = 'locations-source-' + sourceNameAdd;
    const layerName = 'layer-locations-' + sourceNameAdd;
    const layerTextName = 'layer-locations-text-' + sourceNameAdd;

    const existingSource = this.mapHelperService.GetSourceByName(sourceName);
    
    if (!existingSource) {
      // this.mapHelperService.AddSourceEmptyGeoJson(sourceName, "Ubicaciones/agebs");
      // this.mapHelperService.AddLayerLocationsGeneral(layerName, sourceName, 'icon', "Ubicaciones/agebs", 0.1011);
      // this.mapHelperService.AddLayerText(layerTextName, sourceName, 'description', "Etiqueta de Ubicaciones/agebs", -3);

      this.mapHelperService.AddSourceClusteredEmptyGeoJson(sourceName, "Ubicaciones/agebs", 'bussineLocation.png');
      this.mapHelperService.AddLayerCluster(layerName + "-layerCluster", sourceName, "Clusters de Ubicaciones/agebs");
      this.mapHelperService.AddLayerClusterCount( layerName + "-layerClusterCount", sourceName, "Conteo de Clusters de Ubicaciones/agebs");
      this.mapHelperService.AddLayerUnclusteredPointsV2( layerName, sourceName , null, "Puntos de Ubicaciones/agebs", 0.0811 );
      this.mapHelperService.AddLayerText(layerTextName, sourceName, 'Identifier', "Etiqueta de Ubicaciones/agebs", -2);

      this.mapHelperService.AddSourceEmptyGeoJson(sourceName + '-active', "Punto activo de Ubicaciones/agebs", 'bussineLocationA.png');
      this.mapHelperService.AddLayerLocationsGeneral(layerName + '-active', sourceName + '-active', 'icon', "Punto activo de Ubicaciones/agebs", 0.0811);

      this.addEvent();
    } else {
      console.log("ya existe el source simple");
    }
  }

  addEvent() {
    const sourceName = 'locations-source-' + this.locationSufix;
    const layerName = 'layer-locations-' + this.locationSufix;
    const activeLayerName = 'layer-locations-' + this.locationSufix + '-active';
    const layerTextName = 'layer-locations-text-' + this.locationSufix;

    const mouseClick = (e) => {
      if (e.features.length > 0) {
        const feature = e.features[0];
        const identifier = feature.properties ? feature.properties.Identifier : '';
        const coords = feature.geometry.coordinates;

        if (this.popUp) {
          this.popUp.close();
          this.popUp = null;
        }
        const popupData = {
          identifier: identifier,
          parentCoordX: coords[0],
          parentCoordY: coords[1],
          activeSourceName: sourceName + '-active',
        };
        this.popUp = this.dialog.open(PlaceLocationDetailsPopUpComponent,
          {
            maxWidth: "98vw",
            maxHeight: "92vh",
            autoFocus: false,
            restoreFocus: false,
            hasBackdrop: false,
            disableClose: true,
            data: popupData,
          }
        );
      }

    };

    const layerMouseOver = (e) => {

      const marker = e.features[0].properties;

      this.hoveredMarker = marker;
      this.hoveredMarkerFeature = e.features[0];

      // var activeIcon = this.hoveredMarker.activeIcon as string;
      // var icon = this.hoveredMarker.icon as string;
      var activeIcon = this.hoveredMarker.Icon.replace(".png", "A.png") as string;
      var icon = this.hoveredMarker.Icon as string;

      this.mapHelperService._map.setLayoutProperty(
        layerName,
        "icon-image",
        [
          "case",
          ["==", ["get", "Identifier"], marker.Identifier],
          activeIcon,
          ["get", "Icon"],
        ]
      );

      // cambia el color del nombre
      this.mapHelperService._map.setPaintProperty(layerTextName, "text-color", [
        "case",
        ["==", ["get", "Identifier"], marker.Identifier],
        "#0F010F",
        "#530aa8",
      ]);
    };

    const layerMouseLeave = () => {
      if (this.hoveredMarker) {
        if (!environment.production) console.log("hoveredMarkerId", this.hoveredMarker);

        this.mapHelperService._map.setLayoutProperty(
          layerName,
          "icon-image",
          ["get", "Icon"]
        );
        this.mapHelperService._map.setPaintProperty(
          layerTextName,
          "text-color",
          "#530aa8"
        );

        this.hoveredMarker = null;
      }
    };

    const rightClick = (e) => {
      e.preventDefault();
      if (e.features.length > 0) {
        const feature = e.features[0];
        const lat = feature.geometry ? feature.geometry.coordinates[1] : '';
        const lng = feature.geometry ? feature.geometry.coordinates[0] : '';
        const coords = lat + ", " + lng;
        
        if (coords) {
          navigator.clipboard.writeText(coords).then(() => {
            this.toastrService.info('Coordenadas copiadas al portapapeles');
          }).catch(err => {
            console.error('Error al copiar al portapapeles:', err);
          });
        }
      }
    };

    this.mapHelperService._map.on('click', layerName, mouseClick);
    this.mapHelperService._map.on('contextmenu', layerName, rightClick);
    this.mapHelperService._map.on('mouseenter', layerName, layerMouseOver);
    this.mapHelperService._map.on('mouseleave', layerName, layerMouseLeave);
  }

  loadMoreLocationGeoJson(geoJson: any): void {
    const sourceName = 'locations-source-' + this.locationSufix;
    const layerName = 'layer-locations-' + this.locationSufix;
    const layerTextName = 'layer-locations-text-' + this.locationSufix;
    
    this.mapHelperService.UpdateLayerGeoJson(sourceName, geoJson);

  }

  sendLocationsToTop(): void {
    const sourceName = 'locations-source-' + this.locationSufix;
    const layerName = 'layer-locations-' + this.locationSufix;
    const layerTextName = 'layer-locations-text-' + this.locationSufix;

    this.mapHelperService.MoveLayerToTop(layerName);
    this.mapHelperService.MoveLayerToTop(layerTextName);
    this.mapHelperService.MoveLayerToTop(layerName + '-active');
  }

  clearLocations(): void {
    const sourceName = 'locations-source-' + this.locationSufix;
    const layerName = 'layer-locations-' + this.locationSufix;
    const layerTextName = 'layer-locations-text-' + this.locationSufix;
    this.mapHelperService.UpdateLayerGeoJson(sourceName, this.mapHelperService.Empty_GeoJson);
    this.mapHelperService.UpdateLayerGeoJson(sourceName + '-active', this.mapHelperService.Empty_GeoJson);

    if (this.popUp) {
      this.popUp.close();
      this.popUp = null;
    }
  }


  // métodos para obtener nombres
  getSourceName(levelIndex: number): string {
    return this.sourcePrefix + levelIndex;
  }

  getLayerName(levelIndex: number): string {
    return this.layerPrefix + levelIndex;
  }

  getLayerLineName(levelIndex: number): string {
    return this.layerLinePrefix + levelIndex;
  }

  getLayerTextName(levelIndex: number): string {
    return this.layerTextPrefix + levelIndex;
  }

  private getColorForLevel(levelIndex: number, colors: string[]): string {
    return colors[levelIndex % colors.length];
  }

  destroy(): void {
    this.isInitialized = false;
    this.clearLocations();
  }
}
